import { useOne } from "@refinedev/core";
import { Button, Typography, Spin, Empty, Card, Table, Tag, Space, Breadcrumb } from "antd";
import { ArrowLeftOutlined, CalculatorOutlined, EyeOutlined, EyeInvisibleOutlined, HomeOutlined, EnvironmentOutlined, UserOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, useParams } from "react-router";
import { useCallback, useState, useRef } from "react";
import L from "leaflet";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import CustomMap from "../../components/maps/CustomMap";

interface ReferencePlot {
  id: number;
  sigpac_ref: string | null;
  surface: number;
  poligono: number | null;
  parcela: number | null;
  recinto: number | null;
  estimated_planting_date: string | null;
  /**
   * Backend devuelve la geometría como GeoJSON ya parseado en el
   * campo `geom_geojson` (ver FarmDetailSerializer.get_reference_plots).
   * Antes esta interfaz declaraba `geom: string | null` y el render
   * leía `plot.geom` — como el campo no existe, el mapa se quedaba
   * en blanco silenciosamente. Fix: alinear el nombre con el API.
   */
  geom_geojson: GeoJSON.Geometry | null;
}

interface RealPlot {
  id: number;
  name: string;
  surface: number;
  surface_gps: number;
  plants: number | null;
  crop_date: number | null;
  variety: string | null;
  plot_type_name: string | null;
  rootstock_name: string | null;
  plantation_frame: string | null;
  /** Ver comentario en ReferencePlot.geom_geojson — mismo bug. */
  geom_geojson: GeoJSON.Geometry | null;
}

interface FarmDetail {
  id: number;
  name: string;
  town_name: string;
  province_name: string;
  producer_name: string;
  reference_plots: ReferencePlot[];
  real_plots: RealPlot[];
}

const PLOT_STYLE: L.PathOptions = { color: "#1677ff", weight: 3, fillOpacity: 0.3, fillColor: "#1677ff" };
const PLOT_HIGHLIGHT_STYLE: L.PathOptions = { color: "#fa8c16", weight: 4, fillOpacity: 0.5, fillColor: "#fa8c16" };
const REAL_PLOT_STYLE: L.PathOptions = { color: "#722ed1", weight: 2, fillOpacity: 0.2, fillColor: "#722ed1", dashArray: "5, 5" };

const calculateLatLngsAreaHa = (latlngs: L.LatLng[]): number => {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const R = 6371000;
  let area = 0;
  for (let i = 0; i < latlngs.length; i++) {
    const p1 = latlngs[i];
    const p2 = latlngs[(i + 1) % latlngs.length];
    area += toRad(p2.lng - p1.lng) * (2 + Math.sin(toRad(p1.lat)) + Math.sin(toRad(p2.lat)));
  }
  return Math.abs((area * R * R) / 2) / 10000;
};

const calculateGeoJSONAreaHa = (
  input: GeoJSON.Geometry | string | null,
): number | null => {
  if (!input) return null;
  try {
    const geom = typeof input === "string" ? JSON.parse(input) : input;
    const rings: number[][][] = [];
    if (geom.type === "Polygon") rings.push(...geom.coordinates);
    else if (geom.type === "MultiPolygon") geom.coordinates.forEach((poly: number[][][]) => rings.push(...poly));
    else return null;
    const toRad = (d: number) => (d * Math.PI) / 180;
    const R = 6371000;
    let totalArea = 0;
    rings.forEach((ring, idx) => {
      let ringArea = 0;
      for (let i = 0; i < ring.length - 1; i++) {
        const p1 = ring[i];
        const p2 = ring[(i + 1) % (ring.length - 1)];
        ringArea += toRad(p2[0] - p1[0]) * (2 + Math.sin(toRad(p1[1])) + Math.sin(toRad(p2[1])));
      }
      ringArea = Math.abs((ringArea * R * R) / 2);
      totalArea += idx === 0 ? ringArea : -ringArea;
    });
    return Math.abs(totalArea) / 10000;
  } catch { return null; }
};

export const FarmMap = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  const { query } = useOne<FarmDetail>({ resource: "farms", id: id! });
  const record = query.data?.data;
  const isLoading = query.isLoading;
  const fromState = (location.state as any)?.from;

  const [selectedPlotId, setSelectedPlotId] = useState<number | null>(null);
  const [calculatedAreas, setCalculatedAreas] = useState<Record<number, number | null>>({});
  const [showCalculated, setShowCalculated] = useState(false);
  const [drawnArea, setDrawnArea] = useState<number | null>(null);
  const [hiddenPlots, setHiddenPlots] = useState<Set<string>>(new Set());
  const mapInstanceRef = useRef<L.Map | null>(null);
  const layerMapRef = useRef<Record<string, L.Layer>>({});

  const togglePlotVisibility = (key: string) => {
    setHiddenPlots((prev) => {
      const next = new Set(prev);
      const layer = layerMapRef.current[key];
      if (!layer || !mapInstanceRef.current) return next;
      if (next.has(key)) {
        next.delete(key);
        mapInstanceRef.current.addLayer(layer);
      } else {
        next.add(key);
        mapInstanceRef.current.removeLayer(layer);
      }
      return next;
    });
  };

  const handleMapReady = useCallback(
    (map: L.Map) => {
      mapInstanceRef.current = map;
      const hasRefPlots = record?.reference_plots?.length;
      const hasRealPlots = record?.real_plots?.length;
      if (!hasRefPlots && !hasRealPlots) return;

      const refGroup = new L.FeatureGroup();
      const realGroup = new L.FeatureGroup();
      let selectedLayer: L.Layer | null = null;
      const townName = record?.town_name || "";
      const provinceName = record?.province_name || "";
      const locationLabel = townName && provinceName ? `${townName} (${provinceName})` : townName || provinceName || "-";

      // Reference plots (blue)
      record?.reference_plots?.forEach((plot: ReferencePlot) => {
        if (!plot.geom_geojson) return;
        try {
          const feature: GeoJSON.Feature = {
            type: "Feature",
            properties: { id: plot.id },
            geometry: plot.geom_geojson,
          };
          const layer = L.geoJSON(feature, {
            style: PLOT_STYLE,
            onEachFeature: (_f, l) => {
              l.bindPopup(
                `<div style="font-size:13px;">` +
                `<strong>📋 Parcela #${plot.id}</strong><br/>` +
                `${locationLabel}<br/>` +
                `Superficie: <strong>${plot.surface?.toFixed(4) ?? "-"} ha</strong><br/>` +
                `Polígono: ${plot.poligono ?? "-"}<br/>` +
                `Parcela: ${plot.parcela ?? "-"}<br/>` +
                `Recinto: ${plot.recinto ?? "-"}<br/>` +
                `Est. Plantación: ${plot.estimated_planting_date || "-"}` +
                `</div>`
              );
              l.on("click", () => {
                if (selectedLayer && selectedLayer !== l) (selectedLayer as L.GeoJSON).setStyle(PLOT_STYLE);
                (l as L.GeoJSON).setStyle(PLOT_HIGHLIGHT_STYLE);
                selectedLayer = l;
                setSelectedPlotId(plot.id);
              });
            },
          });
          layerMapRef.current[`ref-${plot.id}`] = layer;
          refGroup.addLayer(layer);
        } catch (err) { console.warn("Failed to render ref plot:", plot.id, err); }
      });

      // Real plots (purple dashed)
      record?.real_plots?.forEach((plot: RealPlot) => {
        if (!plot.geom_geojson) return;
        try {
          const feature: GeoJSON.Feature = {
            type: "Feature",
            properties: { id: plot.id },
            geometry: plot.geom_geojson,
          };
          const layer = L.geoJSON(feature, {
            style: REAL_PLOT_STYLE,
            onEachFeature: (_f, l) => {
              l.bindPopup(
                `<div style="font-size:13px;">` +
                `<strong>🌿 ${plot.name}</strong><br/>` +
                `Superficie: <strong>${plot.surface?.toFixed(4) ?? "-"} ha</strong><br/>` +
                `Año Plantación: ${plot.crop_date ?? "-"}<br/>` +
                `Marco: ${plot.plantation_frame || "-"}<br/>` +
                `Plantas: ${plot.plants ?? "-"}<br/>` +
                `Patrón: ${plot.rootstock_name || "-"}` +
                `</div>`
              );
            },
          });
          layerMapRef.current[`real-${plot.id}`] = layer;
          realGroup.addLayer(layer);
        } catch (err) { console.warn("Failed to render real plot:", plot.id, err); }
      });

      refGroup.addTo(map);
      realGroup.addTo(map);

      map.eachLayer((layer: any) => {
        if (layer._layers && layer.addOverlay) {
          layer.addOverlay(refGroup, "Parcelas Declaradas");
          layer.addOverlay(realGroup, "Parcelas Reales");
        }
      });

      map.on("click", () => {
        if (selectedLayer) { (selectedLayer as L.GeoJSON).setStyle(PLOT_STYLE); selectedLayer = null; setSelectedPlotId(null); }
      });

      const allBounds = new L.FeatureGroup([refGroup, realGroup]);
      requestAnimationFrame(() => {
        map.invalidateSize();
        const bounds = allBounds.getBounds();
        if (bounds.isValid()) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 17 });
      });

      // Draw control
      const drawnItems = new L.FeatureGroup();
      map.addLayer(drawnItems);
      const drawControl = new L.Control.Draw({
        position: "topleft",
        edit: { featureGroup: drawnItems, remove: true },
        draw: {
          polygon: { allowIntersection: false, showArea: false, metric: true, shapeOptions: { color: "#52c41a", weight: 3, fillOpacity: 0.25, fillColor: "#52c41a" } },
          polyline: false, rectangle: false, circle: false, marker: false, circlemarker: false,
        },
      });
      map.addControl(drawControl);

      map.on(L.Draw.Event.CREATED, (e: any) => {
        drawnItems.clearLayers();
        const layer = e.layer as L.Polygon;
        drawnItems.addLayer(layer);
        const latlngs = layer.getLatLngs()[0] as L.LatLng[];
        const areaHa = calculateLatLngsAreaHa(latlngs);
        setDrawnArea(areaHa);
        layer.bindPopup(`<strong>📐 Medición</strong><br/>Superficie: <strong>${areaHa.toFixed(4)} ha</strong><br/><em>${(areaHa * 10000).toFixed(0)} m²</em>`).openPopup();
      });
      map.on(L.Draw.Event.EDITED, (e: any) => {
        (e as any).layers.eachLayer((layer: L.Polygon) => {
          const latlngs = layer.getLatLngs()[0] as L.LatLng[];
          const areaHa = calculateLatLngsAreaHa(latlngs);
          setDrawnArea(areaHa);
          layer.setPopupContent(`<strong>📐 Medición</strong><br/>Superficie: <strong>${areaHa.toFixed(4)} ha</strong><br/><em>${(areaHa * 10000).toFixed(0)} m²</em>`).openPopup();
        });
      });
      map.on(L.Draw.Event.DELETED, () => setDrawnArea(null));
    },
    [record]
  );

  const getBreadcrumbItems = () => {
    const items: any[] = [
      {
        title: <HomeOutlined />,
        href: "/",
        onClick: (e: any) => { e.preventDefault(); navigate("/"); },
      },
    ];

    if (fromState === "producer") {
      items.push({
        title: <><UserOutlined /> {t("producers.title", "Productores")}</>,
        href: "/producers",
        onClick: (e: any) => { e.preventDefault(); navigate("/producers"); },
      });
    } else {
      items.push({
        title: <><EnvironmentOutlined /> {t("farms.title", "Fincas")}</>,
        href: "/farms",
        onClick: (e: any) => { e.preventDefault(); navigate("/farms"); },
      });
    }

    items.push({
      title: record?.name || `Finca #${id}`,
      href: `/farms/show/${id}`,
      onClick: (e: any) => { e.preventDefault(); navigate(`/farms/show/${id}`, { state: { from: fromState } }); },
    });

    items.push({ title: t("farms.show.map_title", "Mapa de Parcelas") });
    return items;
  };

  const handleBack = () => navigate(`/farms/show/${id}`, { state: { from: fromState } });

  const handleCalculateAreas = () => {
    if (!record?.reference_plots) return;
    const areas: Record<number, number | null> = {};
    record.reference_plots.forEach((p) => { areas[p.id] = calculateGeoJSONAreaHa(p.geom_geojson); });
    setCalculatedAreas(areas);
    setShowCalculated(true);
  };

  if (isLoading) return <div style={{ textAlign: "center", padding: 48 }}><Spin size="large" /></div>;

  if (!record?.reference_plots?.length && !record?.real_plots?.length) {
    return (
      <div>
        <Breadcrumb style={{ marginBottom: 12 }} items={getBreadcrumbItems()} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <Typography.Title level={4} style={{ margin: 0 }}>{record?.producer_name ? `${record.producer_name} — ` : ""}{record?.name} — {t("farms.show.map_title", "Mapa de Parcelas")}</Typography.Title>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>{t("common.back", "Volver")}</Button>
        </div>
        <Empty description={t("farms.show.no_plots", "No hay parcelas con geometría")} />
      </div>
    );
  }

  const totalSurface = (record?.reference_plots || []).reduce((s, p) => s + (p.surface || 0), 0);
  const totalCalculated = showCalculated ? (record?.reference_plots || []).reduce((s, p) => s + (calculatedAreas[p.id] || 0), 0) : null;
  const formatNum = (val: number | null | undefined, decimals = 4) =>
    val != null ? val.toLocaleString("es-ES", { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) : "-";

  return (
    <div>
      <Breadcrumb style={{ marginBottom: 12 }} items={getBreadcrumbItems()} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          {record!.producer_name ? `${record!.producer_name} — ` : ""}{record!.name} — {t("farms.show.map_title", "Mapa de Parcelas")}
        </Typography.Title>
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>{t("common.back", "Volver")}</Button>
      </div>

      <Card size="small" title={t("farms.map.map_card_title", "Mapa")} style={{ marginBottom: 16 }}>
        <CustomMap onMapReady={handleMapReady} height="550px" showSearchBar={false} showAddressSearch={false} showLayerControl={true} />
        {drawnArea != null && (
          <div style={{ marginTop: 8, padding: "8px 12px", background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 6 }}>
            <Typography.Text strong style={{ color: "#52c41a" }}>
              📐 {t("farms.map.drawn_area", "Superficie trazada")}: {drawnArea.toFixed(4)} ha ({(drawnArea * 10000).toFixed(0)} m²)
            </Typography.Text>
          </div>
        )}
      </Card>

      {/* Reference Plots Table */}
      {record!.reference_plots && record!.reference_plots.length > 0 && (
        <Card
          size="small"
          title={<Space><span style={{ display: "inline-block", width: 12, height: 12, background: "#1677ff", borderRadius: 2 }} />{`${t("farms.map.plots_title", "Parcelas Declaradas")} (${record!.reference_plots.length})`}</Space>}
          style={{ marginBottom: 16 }}
          extra={<Button icon={<CalculatorOutlined />} onClick={handleCalculateAreas} size="small">{t("farms.map.calculate_surface", "Calcular superficie")}</Button>}
        >
          <Table dataSource={record!.reference_plots} rowKey="id" pagination={false} size="small" scroll={{ x: 700 }}
            rowClassName={(r) => (r.id === selectedPlotId ? "ant-table-row-selected" : "")}>
            <Table.Column dataIndex="sigpac_ref" title={t("farms.map.sigpac_ref", "Ref. SIGPAC")} width={130} render={(val: string | null, plot: ReferencePlot) => val || `#${plot.id}`} sorter={(a: ReferencePlot, b: ReferencePlot) => (a.sigpac_ref || "").localeCompare(b.sigpac_ref || "")} />
            <Table.Column title={t("farms.map.province", "Provincia")} width={120} render={() => record?.province_name || "-"} sorter={() => 0} />
            <Table.Column title={t("farms.map.town", "Municipio")} width={120} render={() => record?.town_name || "-"} sorter={() => 0} />
            <Table.Column dataIndex="poligono" title={t("farms.map.poligono", "Polígono")} width={90} align="center" render={(v: number | null) => v ?? "-"} sorter={(a: ReferencePlot, b: ReferencePlot) => (a.poligono ?? 0) - (b.poligono ?? 0)} />
            <Table.Column dataIndex="parcela" title={t("farms.map.parcela", "Parcela")} width={90} align="center" render={(v: number | null) => v ?? "-"} sorter={(a: ReferencePlot, b: ReferencePlot) => (a.parcela ?? 0) - (b.parcela ?? 0)} />
            <Table.Column dataIndex="recinto" title={t("farms.map.recinto", "Recinto")} width={90} align="center" render={(v: number | null) => v ?? "-"} sorter={(a: ReferencePlot, b: ReferencePlot) => (a.recinto ?? 0) - (b.recinto ?? 0)} />
            <Table.Column dataIndex="surface" title={t("farms.map.surface_registered", "Sup. Registrada (ha)")} width={150} align="right" render={(val: number) => formatNum(val)} sorter={(a: ReferencePlot, b: ReferencePlot) => (a.surface || 0) - (b.surface || 0)} />
            {showCalculated && (
              <Table.Column key="calculated" title={t("farms.map.surface_calculated", "Sup. Calculada (ha)")} width={160} align="right"
                sorter={(a: ReferencePlot, b: ReferencePlot) => (calculatedAreas[a.id] || 0) - (calculatedAreas[b.id] || 0)}
                render={(_: unknown, plot: ReferencePlot) => {
                  const calc = calculatedAreas[plot.id];
                  if (calc == null) return "-";
                  const diff = plot.surface > 0 ? ((calc - plot.surface) / plot.surface) * 100 : 0;
                  const color = Math.abs(diff) < 5 ? "#52c41a" : Math.abs(diff) < 15 ? "#fa8c16" : "#f5222d";
                  return (<Space size={4}><span>{formatNum(calc)}</span><Tag color={color} style={{ fontSize: 11 }}>{diff >= 0 ? "+" : ""}{diff.toFixed(1)}%</Tag></Space>);
                }}
              />
            )}
            <Table.Column dataIndex="estimated_planting_date" title={t("farms.map.planting_date", "Fecha Est. Plantación")} width={150} render={(v: string | null) => v || "-"} sorter={(a: ReferencePlot, b: ReferencePlot) => (a.estimated_planting_date || "").localeCompare(b.estimated_planting_date || "")} />
            <Table.Column title={t("common.actions", "Acciones")} width={80} align="center" fixed="right"
              render={(_: unknown, plot: ReferencePlot) => {
                const key = `ref-${plot.id}`;
                const hidden = hiddenPlots.has(key);
                return plot.geom_geojson ? (
                  <Button type="text" size="small"
                    icon={hidden ? <EyeInvisibleOutlined style={{ color: "#bfbfbf" }} /> : <EyeOutlined style={{ color: "#1677ff" }} />}
                    onClick={() => togglePlotVisibility(key)} title={hidden ? "Mostrar en mapa" : "Ocultar en mapa"} />
                ) : null;
              }}
            />
          </Table>
          <div style={{ display: "flex", gap: 24, padding: "12px 8px 0", borderTop: "1px solid #f0f0f0", marginTop: 8 }}>
            <Typography.Text strong>{t("farms.map.total_registered", "Total registrada")}: {formatNum(totalSurface)} ha</Typography.Text>
            {showCalculated && totalCalculated != null && (
              <Typography.Text strong>{t("farms.map.total_calculated", "Total calculada")}: {formatNum(totalCalculated)} ha</Typography.Text>
            )}
          </div>
        </Card>
      )}

      {/* Real Plots Table */}
      {record!.real_plots && record!.real_plots.length > 0 && (
        <Card
          size="small"
          title={<Space><span style={{ display: "inline-block", width: 12, height: 12, background: "#722ed1", borderRadius: 2, border: "1px dashed #722ed1" }} />{`${t("farms.map.real_plots_title", "Parcelas Reales")} (${record!.real_plots.length})`}</Space>}
        >
          <Table dataSource={record!.real_plots} rowKey="id" pagination={false} size="small" scroll={{ x: 800 }}>
            <Table.Column dataIndex="name" title={t("farms.map.plot_name", "Nombre")} width={120} sorter={(a: RealPlot, b: RealPlot) => (a.name || "").localeCompare(b.name || "")} />
            <Table.Column dataIndex="surface" title={t("farms.map.surface_audited", "Sup. Auditada (ha)")} width={140} align="right" render={(v: number) => formatNum(v)} sorter={(a: RealPlot, b: RealPlot) => (a.surface || 0) - (b.surface || 0)} />
            <Table.Column dataIndex="surface_gps" title={t("farms.map.surface_gps", "Sup. GPS (ha)")} width={130} align="right" render={(v: number) => formatNum(v)} sorter={(a: RealPlot, b: RealPlot) => (a.surface_gps || 0) - (b.surface_gps || 0)} />
            <Table.Column dataIndex="crop_date" title={t("farms.map.crop_date", "Año Plant.")} width={100} align="center" render={(v: number | null) => v ?? "-"} sorter={(a: RealPlot, b: RealPlot) => (a.crop_date ?? 0) - (b.crop_date ?? 0)} />
            <Table.Column dataIndex="plantation_frame" title={t("farms.map.plantation_frame", "Marco")} width={100} align="center" render={(v: string | null) => v ?? "-"} sorter={(a: RealPlot, b: RealPlot) => (a.plantation_frame || "").localeCompare(b.plantation_frame || "")} />
            <Table.Column dataIndex="plants" title={t("farms.map.trees", "Árboles")} width={80} align="right" render={(v: number | null) => v ?? "-"} sorter={(a: RealPlot, b: RealPlot) => (a.plants ?? 0) - (b.plants ?? 0)} />
            <Table.Column dataIndex="rootstock_name" title={t("farms.map.rootstock", "Patrón")} width={130} render={(v: string | null) => v ?? "-"} sorter={(a: RealPlot, b: RealPlot) => (a.rootstock_name || "").localeCompare(b.rootstock_name || "")} />
            <Table.Column title={t("common.actions", "Acciones")} width={80} align="center" fixed="right"
              render={(_: unknown, plot: RealPlot) => {
                const key = `real-${plot.id}`;
                const hidden = hiddenPlots.has(key);
                return plot.geom_geojson ? (
                  <Button type="text" size="small"
                    icon={hidden ? <EyeInvisibleOutlined style={{ color: "#bfbfbf" }} /> : <EyeOutlined style={{ color: "#722ed1" }} />}
                    onClick={() => togglePlotVisibility(key)} title={hidden ? "Mostrar en mapa" : "Ocultar en mapa"} />
                ) : null;
              }}
            />
          </Table>
          <div style={{ display: "flex", gap: 24, padding: "12px 8px 0", borderTop: "1px solid #f0f0f0", marginTop: 8 }}>
            <Typography.Text strong>{t("farms.map.total_audited", "Total auditada")}: {formatNum(record!.real_plots.reduce((s, p) => s + (p.surface || 0), 0))} ha</Typography.Text>
          </div>
        </Card>
      )}
    </div>
  );
};
